import { useEffect, useState } from 'react'
import FileUploadAllLineService from '../shared/services/file-upload-all-lines/file-upload-all-lines.service'

export interface ValidationSummary {
  total: number
  success: number
  failed: number
}

export function useValidationSummary(
  uploadResult: any,
  typeOfData: string,
  refreshTrigger?: number,
) {
  const [summary, setSummary] = useState<ValidationSummary | null>(null)
  const [loading, setLoading] = useState(false)
  const fileUploadAllLineService = new FileUploadAllLineService()

  const resolvedFileType = typeOfData
  const isLinkedActivityImport = uploadResult?.linkToExistingEmission === true
    && (resolvedFileType === 'emissions-rules' || resolvedFileType === 'emissions-manual')

  useEffect(() => {
    if (!uploadResult || !uploadResult.id || !typeOfData) {
      setSummary(null)
      return
    }

    let cancelled = false
    setLoading(true)
    const fetchCount = async (ft: string, status: 'matched' | 'pending' | 'reconciliation'): Promise<number> => {
      try {
        const resp = await fileUploadAllLineService.getUploadLinesWithPaginationByFile(ft, uploadResult.id, status, 0, 1)
        if (typeof resp?.count === 'number') {
          return resp.count
        }
        return 0
      } catch {
        return 0
      }
    }

    const fetchTotalsWithoutStatus = async (ft: string): Promise<number> => {
      try {
        const [matchedResp, pendingResp] = await Promise.all([
          fileUploadAllLineService.getUploadLinesWithPaginationByFile(ft, uploadResult.id, 'matched', 0, 1).catch(() => null),
          fileUploadAllLineService.getUploadLinesWithPaginationByFile(ft, uploadResult.id, 'pending', 0, 1).catch(() => null)
        ])
        const matchedCount = typeof matchedResp?.count === 'number' ? matchedResp.count : 0
        const pendingCount = typeof pendingResp?.count === 'number' ? pendingResp.count : 0
        return matchedCount + pendingCount
      } catch {
        return 0
      }
    }

    const fetchSummary = async () => {
      try {
        const [matched, pending] = await Promise.all([
          fetchCount(resolvedFileType, isLinkedActivityImport ? 'reconciliation' : 'matched'),
          fetchCount(resolvedFileType, 'pending')
        ])
        if (cancelled) return
        let total = matched + pending
        let success = matched
        let failed = pending
        if (total === 0) {
          const tot = await fetchTotalsWithoutStatus(resolvedFileType)
          if (cancelled) return
          if (tot > 0) {
            total = tot
            success = typeof uploadResult?.mapped === 'boolean' && uploadResult.mapped ? tot : 0
            failed = tot - success
          }
        }
        if (!cancelled) {
          setSummary({ total, success, failed })
        }
      } catch (err) {
        console.log(err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchSummary()
    return () => {
      cancelled = true
    }
  }, [uploadResult, resolvedFileType, refreshTrigger, isLinkedActivityImport])

  return { summary, loading }
}


